import type {
  AbortExcelParams,
  DownloadExcelParams,
  ExcelJobDTO,
  GenerateExcelBatchBody,
  GenerateExcelBatchResponse,
} from '@psw/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { saveAs } from 'file-saver';
import { produce } from 'immer';
import { FetchService, queryClient } from '../services/fetch.service';
import { type PswMutationOptions, usePswQuery } from './queries';

export const excelQueryKeys = {
  listExcelJobs: ['excel', 'list'] as const,
};

// =====================================================================================================================
// Generate excel
// =====================================================================================================================

export function useGenerateExcel(
  options?: PswMutationOptions<GenerateExcelBatchResponse, GenerateExcelBatchBody, ExcelJobDTO>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: variables => FetchService.post('/excel/batch', variables),
    onSuccess: async (data, variables, onMutateResult, context) => {
      options?.onSuccess?.(data, variables, onMutateResult, context);
      await queryClient.invalidateQueries({ queryKey: excelQueryKeys.listExcelJobs });
    },
  });
}

// =====================================================================================================================
// List excel jobs
// =====================================================================================================================

export function useListExcelJobs() {
  return usePswQuery<ExcelJobDTO[]>({
    queryKey: excelQueryKeys.listExcelJobs,
    queryFn: () => FetchService.get('/excel/jobs'),
  });
}

export function updateExcelJobInCache(job: ExcelJobDTO) {
  const jobsInCache =
    queryClient.getQueryData<ReturnType<typeof useListExcelJobs>['data']>(excelQueryKeys.listExcelJobs) || [];
  queryClient.setQueryData(
    excelQueryKeys.listExcelJobs,
    produce(jobsInCache, draft => {
      const existingJobIndex = draft.findIndex(j => j.id === job.id);
      if (existingJobIndex !== -1) {
        draft[existingJobIndex] = job;
      } else {
        draft.push(job);
      }
    }),
  );
}

// =====================================================================================================================
// Download excel
// =====================================================================================================================

export function useDownloadExcel(options?: PswMutationOptions<Response, DownloadExcelParams>) {
  return useMutation({
    ...options,
    mutationFn: variables => FetchService.rawFetch(`/excel/download/${variables.jobId}`),
    onSuccess: async (data, variables, onMutateResult, context) => {
      options?.onSuccess?.(data, variables, onMutateResult, context);
      saveAs(new Blob([await data.blob()]), 'product_swap_options.xlsx');
    },
  });
}

// =====================================================================================================================
// Abort excel generation
// =====================================================================================================================

export function useAbortExcelGeneration(options?: PswMutationOptions<void, AbortExcelParams>) {
  return useMutation({
    ...options,
    mutationFn: variables => FetchService.delete(`/excel/${variables.jobId}`),
    onSuccess: async (data, variables, onMutateResult, context) => {
      options?.onSuccess?.(data, variables, onMutateResult, context);
      await queryClient.invalidateQueries({ queryKey: excelQueryKeys.listExcelJobs });
    },
  });
}
